import { createContext, useContext, useState, useCallback, useMemo } from "react";
import CopilotDrawer from "./components/CopilotDrawer";
import { Outlet, useParams } from "react-router-dom";
import { useGetMessagesQuery, useAskCopilotMutation } from "@/api/copilotApi";
import { skipToken } from "@reduxjs/toolkit/query/react";

export interface CopilotMessage {
  id: string;
  role: "user" | "model" | "function";
  content: string;
  createdAt: number;
  uid: string;
  functionCall?: {
    name: string;
    args: any;
  };
}

interface CopilotContextType {
  isOpen: boolean;
  toggleDrawer: () => void;
  sendMessage: (text: string) => Promise<void>;
  messages: CopilotMessage[];
  isLoading: boolean;
}

const CopilotContext = createContext<CopilotContextType | undefined>(undefined);

export const useCopilot = () => {
  const context = useContext(CopilotContext);
  if (!context) {
    throw new Error("useCopilot must be used within a CopilotProvider");
  }
  return context;
};

const EMPTY_MESSAGES: CopilotMessage[] = [];

export const CopilotProvider = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const [isOpen, setIsOpen] = useState(false);

  // Usamos una referencia estable (EMPTY_MESSAGES) por defecto para evitar recrear el array en cada render si data es undefined
  const { data: messages = EMPTY_MESSAGES } = useGetMessagesQuery(organizationId || skipToken);
  const [askCopilot, { isLoading }] = useAskCopilotMutation();

  const toggleDrawer = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const sendMessage = useCallback(async (text: string) => {
    if (!organizationId || !text.trim()) return;

    try {
      await askCopilot({ text, organizationId }).unwrap();
    } catch (error) {
      console.error("Error sending message to Copilot:", error);
    }
  }, [organizationId, askCopilot]);

  // Memoizamos el valor del contexto para que sus hijos (Outlet, CopilotDrawer) no hagan re-render a menos que algo realmente cambie
  const contextValue = useMemo(() => ({
    isOpen,
    toggleDrawer,
    sendMessage,
    messages,
    isLoading
  }), [isOpen, toggleDrawer, sendMessage, messages, isLoading]);

  return (
    <CopilotContext.Provider value={contextValue}>
      <Outlet />
      <CopilotDrawer />
    </CopilotContext.Provider>
  );
};
